import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Edit2 } from "lucide-react";

const empty = { name: "", description: "", price: "", discount_amount: 0, customer_points: 0, affiliate_points: 0, image_url: "", active: true };

export default function ServicesTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/services").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const priceRaw = String(form.price).trim();
      const payload = {
        ...form,
        price: priceRaw === "" ? null : Number(priceRaw),
        discount_amount: Number(form.discount_amount),
        customer_points: Number(form.customer_points),
        affiliate_points: Number(form.affiliate_points),
      };
      if (edit) await api.put(`/admin/services/${edit.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Saved");
      setOpen(false); setEdit(null); setForm(empty);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/admin/services/${id}`);
    toast.success("Deleted");
    load();
  };

  const startEdit = (s) => { setEdit(s); setForm({ ...s, price: s.price ?? "" }); setOpen(true); };
  const startNew = () => { setEdit(null); setForm(empty); setOpen(true); };

  const setField = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button data-testid="btn-add-service" onClick={startNew} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> Add service
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} data-testid={`admin-service-${s.id}`} className="velo-card p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="text-white font-medium">{s.name}</div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => startEdit(s)} className="text-slate-400 hover:text-white h-8 w-8 p-0"><Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(s.id)} className="text-slate-400 hover:text-red-400 h-8 w-8 p-0"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-2">{s.description}</div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <div className="velo-surface p-2 rounded"><div className="text-slate-500 uppercase text-[10px]">Price</div><div className="text-white font-mono">{s.price === null || s.price === undefined ? <span className="italic text-slate-500">On request</span> : `₹${Number(s.price).toLocaleString("en-IN")}`}</div></div>
              <div className="velo-surface p-2 rounded"><div className="text-slate-500 uppercase text-[10px]">Discount</div><div className="text-emerald-400 font-mono">{Number(s.discount_amount) > 0 ? `₹${Number(s.discount_amount).toLocaleString("en-IN")}` : "—"}</div></div>
              <div className="velo-surface p-2 rounded"><div className="text-slate-500 uppercase text-[10px]">Customer pts</div><div className="text-pink-400 font-mono">{s.customer_points ?? s.points_value ?? 0}</div></div>
              <div className="velo-surface p-2 rounded"><div className="text-slate-500 uppercase text-[10px]">Affiliate pts</div><div className="text-fuchsia-300 font-mono">{s.affiliate_points ?? s.points_value ?? 0}</div></div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#22103F] border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle>{edit ? "Edit service" : "New service"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label className="text-slate-300">Name</Label><Input data-testid="input-svc-name" value={form.name} onChange={(e) => setField("name", e.target.value)} className="velo-input mt-2" /></div>
            <div className="col-span-2"><Label className="text-slate-300">Description</Label><Input data-testid="input-svc-desc" value={form.description} onChange={(e) => setField("description", e.target.value)} className="velo-input mt-2" /></div>
            <div><Label className="text-slate-300">Price (₹) — optional</Label><Input data-testid="input-svc-price" type="number" value={form.price} onChange={(e) => setField("price", e.target.value)} placeholder="Leave blank if varies (e.g. PPF)" className="velo-input mt-2 font-mono" /></div>
            <div><Label className="text-slate-300">Discount amount (₹)</Label><Input data-testid="input-svc-discount" type="number" value={form.discount_amount} onChange={(e) => setField("discount_amount", e.target.value)} className="velo-input mt-2 font-mono" /></div>
            <div><Label className="text-slate-300">Customer reward points</Label><Input data-testid="input-svc-customer-points" type="number" value={form.customer_points} onChange={(e) => setField("customer_points", e.target.value)} className="velo-input mt-2 font-mono" /><div className="text-[10px] text-slate-500 mt-1">Credited when the referrer is a Customer</div></div>
            <div><Label className="text-slate-300">Affiliate reward points</Label><Input data-testid="input-svc-affiliate-points" type="number" value={form.affiliate_points} onChange={(e) => setField("affiliate_points", e.target.value)} className="velo-input mt-2 font-mono" /><div className="text-[10px] text-slate-500 mt-1">Credited when the referrer is an Affiliate</div></div>
            <div className="col-span-2"><Label className="text-slate-300">Image URL</Label><Input data-testid="input-svc-image" value={form.image_url || ""} onChange={(e) => setField("image_url", e.target.value)} className="velo-input mt-2" /></div>
          </div>
          <DialogFooter><Button data-testid="btn-save-service" onClick={save} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
